import React from 'react';
import { APITypes } from '../../../primary-record-types';
import { getCommaList } from '../../../utils/getCommaList';
import { entitlements } from '../../../utils/sharedMessages';
import { getIntl } from '../../../utils/intl';
import usePayments from '../../../hooks/api/usePayments';
import Alert from '../../v2BaseComponents/Alert';
import useNotify from '../../../hooks/useNotify';
import { useNestedNavigation } from '../../../utils/appNavigationNames';
import Text from '../../v2BaseComponents/Text';
import useRecordEvent from '../../../hooks/useRecordEvent';

export type PendingUrlRegistrationCodeAlertOwnProps = {
    status:
        | APITypes.paymentService.ValidDiscountRegistrationCodeResponse
        | APITypes.paymentService.ValidEntitlementRegistrationCodeResponse;
    code: string;
};

export type PendingUrlRegistrationCodeAlertProps = PendingUrlRegistrationCodeAlertOwnProps;

const intl = getIntl('PendingUrlRegistrationCodeAlert', {
    alertHeader: 'Apply Partner Program Code?',
    entitlementSubHeader: 'Free {entitlement}',
    entitlementWithMonthsSubHeader: 'Free {entitlement} for {months} months',
    percentSubHeader: '{value}% off',
    alertSubText:
        'You used a link with a partner program code "{code}". Would you like to apply it to your account?  The code will grant you:',
    yes: 'Yes, Apply',
    applySuccess: 'Partner Program Code Applied',
});

const getAlertSubHeader = (
    validationStatus:
        | APITypes.paymentService.ValidDiscountRegistrationCodeResponse
        | APITypes.paymentService.ValidEntitlementRegistrationCodeResponse,
    code: string,
) => {
    if (validationStatus.type === 'discount')
        return intl('percentSubHeader', {
            value: validationStatus.discountRate,
            code,
        });
    else if (validationStatus.type === 'entitlement' && validationStatus.durationInMonths)
        return intl('entitlementWithMonthsSubHeader', {
            entitlement: getCommaList(validationStatus.entitlements.map((e) => entitlements(e))),
            months: validationStatus.durationInMonths,
            code: code,
        });
    else
        return intl('entitlementSubHeader', {
            entitlement: getCommaList(validationStatus.entitlements.map((e) => entitlements(e))),
            code: code,
        });
};

const PendingUrlRegistrationCodeAlert: React.FC<PendingUrlRegistrationCodeAlertProps> = ({ status, code }) => {
    const notify = useNotify();
    const { nestedAccountNavigation } = useNestedNavigation();
    const recordEvent = useRecordEvent();
    const { applyRegistrationCode, isApplyingRegistrationCode } = usePayments({
        onRegistrationCodeApplied: (code) => {
            recordEvent('registrationCodeApplied', {
                appliedRegistrationCode: code,
            });
            notify('success', intl('applySuccess'));
            nestedAccountNavigation('subscription', undefined);
        },
    });
    return (
        <>
            <Alert
                title={intl('alertHeader')}
                message={
                    <>
                        <Text variant="body-1">
                            {intl('alertSubText', { code }) + '\n' + getAlertSubHeader(status, code)}
                        </Text>
                    </>
                }
                type="info"
                actionItems={[
                    {
                        title: intl('yes'),
                        onPress: () => {
                            applyRegistrationCode({ code });
                        },
                        isLoadingDisabled: isApplyingRegistrationCode,
                    },
                ]}
                justifyActionItems="center"
            />
        </>
    );
};

export default React.memo(PendingUrlRegistrationCodeAlert);
